import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, InfoWindowF, useJsApiLoader } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

const containerStyle = {
    width: '100%',
    height: '400px'
};

const GoogleMapWithMarkers = ({ mapObject, zoom }) => {
    const [selectedMarker, setSelectedMarker] = useState(null);  // Handle InfoWindow for selected marker
    const mapRef = useRef(null);  // Ref for the Google Map instance

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
    });

    // Use fitBounds to show all markers in the visible map area
    useEffect(() => {
        if (isLoaded && mapRef.current && mapObject.markers.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            mapObject.markers.forEach(marker => {
                bounds.extend(new window.google.maps.LatLng(marker.lat, marker.lng));
            });
            mapRef.current.fitBounds(bounds);  // Adjust map bounds to fit markers
        }
    }, [isLoaded, mapObject]);

    const onLoad = React.useCallback(function callback(map) {
        mapRef.current = map;  // Save the map instance to the ref

        // Create a MarkerClusterer instance with dynamic markers
        const markerCluster = new MarkerClusterer({
            map: mapRef.current,
            markers: mapObject.markers.map((marker) => {
                const googleMarker = new window.google.maps.Marker({
                    position: { lat: marker.lat, lng: marker.lng },
                });

                // Add click event to open InfoWindow
                googleMarker.addListener('click', () => {
                    setSelectedMarker(marker);  // Set selected marker for InfoWindow
                });

                return googleMarker;
            }),
        });
    }, [mapObject.markers]);

    const onUnmount = React.useCallback(function callback(map) {
        mapRef.current = null;
    }, []);

    return isLoaded ? (
        <GoogleMap
            mapContainerStyle={containerStyle}
            center={{ lat: mapObject.map.lat, lng: mapObject.map.lng }}  // Set initial center based on mapObject
            zoom={zoom}  // Initial zoom level
            onLoad={onLoad}
            onUnmount={onUnmount}
        >
            {/* Display InfoWindow if a marker is selected */}
            {selectedMarker && (
                <InfoWindowF
                    position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
                    onCloseClick={() => setSelectedMarker(null)}  // Close InfoWindow
                >
                    <div>
                        <h2>{selectedMarker.infoWindowTitle}</h2>
                        <p>{selectedMarker.infoWindowDesc}</p>
                        <p>
                            <a href={selectedMarker.infoWindowLink} target="_blank" rel="noopener noreferrer">
                                More Info
                            </a>
                        </p>
                    </div>
                </InfoWindowF>
            )}
        </GoogleMap>
    ) : <div>Loading map...</div>;
}

export default GoogleMapWithMarkers;
